
import { useEffect } from 'react';
import { useLaneOptions } from '../../../hooks/useLaneOptions';

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: {
    lane: string;
    city: string;
    verified: boolean;
    hasNewRelease: boolean;
    hasMemberships: boolean;
    roles: string;
  };
  onFilterChange: (key: string, value: string | boolean) => void;
  onClearFilters: () => void;
  lanes?: string[];
}

const cities = [
  'Nairobi',
  'Mombasa',
  'Kisumu',
  'Eldoret',
  'Nakuru',
  'Thika'
];

const roles = [
  'MC',
  'Producer',
  'DJ',
  'Collective'
];

export default function FilterDrawer({
  isOpen,
  onClose,
  filters,
  onFilterChange,
  onClearFilters,
  lanes,
}: FilterDrawerProps) {
  const { lanes: laneOptions } = useLaneOptions({
    initialLanes: lanes,
    skipFetch: Boolean(lanes && lanes.length > 0),
  });
  const effectiveLanes = lanes && lanes.length > 0 ? lanes : laneOptions;
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const hasActiveFilters = Object.values(filters).some(value => 
    value !== '' && value !== false
  );

  const handleApply = () => {
    onClose();
  };

  const handleReset = () => {
    onClearFilters();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-sm bg-white shadow-xl">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Refine</h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-6">
              {/* Lane/Genre */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Lane/Genre</h4>
                <div className="space-y-2">
                  {effectiveLanes.map((lane) => (
                    <label key={lane} className="flex items-center">
                      <input
                        type="radio"
                        name="lane"
                        value={lane}
                        checked={filters.lane === lane}
                        onChange={(e) => onFilterChange('lane', e.target.checked ? lane : '')}
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">{lane}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* City/Scene */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">City/Scene</h4>
                <div className="space-y-2">
                  {cities.map((city) => (
                    <label key={city} className="flex items-center">
                      <input
                        type="radio"
                        name="city"
                        value={city}
                        checked={filters.city === city}
                        onChange={(e) => onFilterChange('city', e.target.checked ? city : '')}
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">{city}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Status */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Status</h4>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.verified}
                      onChange={(e) => onFilterChange('verified', e.target.checked)}
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Verified only</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.hasNewRelease}
                      onChange={(e) => onFilterChange('hasNewRelease', e.target.checked)}
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Has new release</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.hasMemberships}
                      onChange={(e) => onFilterChange('hasMemberships', e.target.checked)}
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Has memberships</span>
                  </label>
                </div>
              </div>

              {/* Roles */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Roles</h4>
                <div className="space-y-2">
                  {roles.map((role) => (
                    <label key={role} className="flex items-center">
                      <input
                        type="radio"
                        name="roles"
                        value={role}
                        checked={filters.roles === role}
                        onChange={(e) => onFilterChange('roles', e.target.checked ? role : '')}
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">{role}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex gap-3">
              <button
                onClick={handleReset}
                disabled={!hasActiveFilters}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Reset
              </button>
              <button
                onClick={handleApply}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 whitespace-nowrap"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
